import React, { memo, useCallback, useMemo } from 'react';
import { Carousel, ProductCard } from '@dsg-tech/homefield-react-cdk';
import { TextLink } from '@dsg-tech/homefield-react';
import { checkStores } from 'utility/utility';
import { SearchProductResult, SearchState } from '../../Search/types';

type ProductSuggestionsProps = {
  productSuggestions: SearchState['productResults'];
  searchTerm: string;
  thumbnailApi: string;
  getProductImage: (product: SearchProductResult, thumbnailApi: string) => string;
  getSeoURL: (product: SearchProductResult) => string;
  evaluateViewAllUrl: () => string;
};

type ProductData = SearchProductResult & {
  image: string;
  link: string;
  price: string;
  originalPrice: string;
};

const ProductSuggestions = ({
  productSuggestions,
  searchTerm,
  thumbnailApi,
  getProductImage,
  getSeoURL,
  evaluateViewAllUrl,
}: ProductSuggestionsProps) => {
  const getProductPrice = useCallback((product: SearchProductResult) => {
    const {
      maxListPrice,
      minListPrice,
      maxOfferPrice,
      minOfferPrice,
      mapPriceIndicator,
      priceIndicator,
    } = product;
    const seePriceInCart = mapPriceIndicator === 1;
    const isClearance = priceIndicator === 0;

    const calculatePriceRange = (minPrice: number, maxPrice: number) => {
      return maxPrice !== minPrice
        ? `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`
        : `$${minPrice.toFixed(2)}`;
    };

    const getOriginalPrice = () => {
      if (minListPrice && maxListPrice) {
        return calculatePriceRange(minListPrice, maxListPrice);
      }
      return '';
    };

    const getOfferPrice = () => {
      if (minOfferPrice && maxOfferPrice) {
        return calculatePriceRange(minOfferPrice, maxOfferPrice);
      }
      return '';
    };

    let price = '';
    let originalPrice = '';

    if (seePriceInCart) {
      price = 'See Price In Cart';
      originalPrice = getOriginalPrice();
    } else {
      price = getOfferPrice();
      if (isClearance) {
        originalPrice = getOriginalPrice();
      }
    }

    return { price, originalPrice };
  }, []);

  const productData: ProductData[] = useMemo(() => {
    return Array.isArray(productSuggestions)
      ? productSuggestions.map((product) => ({
          ...product,
          image: getProductImage(product, thumbnailApi),
          link: getSeoURL(product),
          price: getProductPrice(product).price,
          originalPrice: getProductPrice(product).originalPrice,
        }))
      : [];
  }, [productSuggestions, thumbnailApi, getProductPrice]);

  return (
    <div className="sayt-product-suggestions-container" data-testid="sayt-product-suggestions">
      <div className="hmf-display-lg-flex hmf-justify-content-between hmf-align-items-center hmf-pt-s hmf-pb-xxs hmf-px-s hmf-display-none">
        <h1 className="hmf-body-bold-l hmf-m-0">Top Products for &quot;{searchTerm}&quot;</h1>
        <TextLink
          href={checkStores(evaluateViewAllUrl())}
          className="hmf-body-bold-l"
          variant="secondary">
          View All
        </TextLink>
      </div>
      <div className="hmf-display-lg-flex hmf-display-none">
        <Carousel showHeader={false} showTitle={false} showScrollbar showScrollButtons={false}>
          {productData.map((product: ProductData) => {
            return (
              <div
                key={product.partNumber}
                data-testid={`desktop-product-suggestion-${product.partNumber}`}>
                <ProductCard
                  link={product.link}
                  image={product.image}
                  alt="product thumbnail"
                  name={product.name}
                  price={product.price}
                  originalPrice={product.originalPrice}
                  variant="vertical"
                  background
                  size="small"
                  loading={false}
                  className="product-card-desktop"
                />
              </div>
            );
          })}
        </Carousel>
      </div>
      <div className="hmf-display-block hmf-display-lg-none">
        {productData.map((product: ProductData) => {
          return (
            <a
              key={product.partNumber}
              className="hmf-display-flex hmf-align-items-center hmf-py-xxs hmf-px-s hmf-text-decoration-none label-base product-card-mobile"
              data-testid={`mobile-product-suggestion-${product.partNumber}`}
              href={checkStores(product.link)}
              target="_self"
              aria-label="View Product Details">
              <img src={product.image} alt="product thumbnail" className="hmf-mr-xs" />
              <div className="hmf-body-l">{product.name}</div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default memo(
  ProductSuggestions,
  (prev, next) => prev.productSuggestions === next.productSuggestions,
);
